import ComponentPlaceholder from 'components/ComponentPlaceholder';
import type { IconName } from 'components/Icon';
import { IntrinsicSizeImg } from 'components/Img';
import { LayoutContainer } from 'components/Layout';
import Message, { mapSeverityToVariant } from 'components/Message';
import { getValidSitecoreLink, ScLink } from 'components/SitecoreLink';
import type { DigizuiteAsset } from 'models/asset';
import type {
	Field,
	MessageSeverity,
	SitecoreBlockProps,
	SitecoreLink,
} from 'models/sitecore';
import { getEditorMargin } from 'utils/business-logic';
import { getAsset } from 'utils/helpers';

interface Props extends SitecoreBlockProps {
	fields?: {
		assetIcon?: Field<DigizuiteAsset[]>;
		bottomImage?: Field<DigizuiteAsset[]>;
		heading?: Field<string>;
		libraryIcon?: Field<IconName>;
		link?: Field<SitecoreLink>;
		severity?: Field<MessageSeverity>;
		text?: Field<string>;
	};
}

export default function InformationNotice({ fields, params }: Props) {
	if (!fields) {
		return null;
	}

	const heading = fields.heading?.value;
	const icon =
		fields.libraryIcon?.value ||
		getAsset('Source Copy', fields.assetIcon?.value)?.src;
	const img = getAsset('Source Copy', fields.bottomImage?.value);
	const link = getValidSitecoreLink(fields.link?.value);
	const text = fields.text?.value;
	const severity = fields.severity?.value;

	if (!text || !icon) {
		return (
			<ComponentPlaceholder
				className={getEditorMargin(params)}
				componentName="InformationNotice"
				description="The component requires an icon and a text"
			/>
		);
	}

	return (
		<LayoutContainer outerClassName={getEditorMargin(params)}>
			<Message
				className="max-w-col-4"
				icon={icon}
				heading={heading}
				variant={mapSeverityToVariant(severity)}
			>
				<p>{text}</p>
				{img && (
					<IntrinsicSizeImg
						asset={img}
						className="mt-4 block max-h-6 w-auto"
						loading="eager"
					/>
				)}
				{link && (
					<p className="mt-4">
						<ScLink link={link} underline>
							{link.text}
						</ScLink>
					</p>
				)}
			</Message>
		</LayoutContainer>
	);
}
InformationNotice.displayName = 'InformationNotice';
